import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getLocalMerchantId, resetLocalSettings, updateLocalSettings } from 'components/settings/localStore';
import { ISettings } from 'components/settings/model/Settings';
import { settingsApi } from 'components/settings/settingsApi';
import { Timeslot } from 'components/timeslots/model/Timeslot';
import { timeslotsApi } from 'components/timeslots/timeslotsApi';
import { getLoadingImagesFolder } from 'lib/imageHelpers';
import logger from 'lib/logger';
import { setTimeslot } from 'store/basket/basketSlice';
import { enqueueSnackbar } from 'notistack';

export type SettingsStateSettings = ISettings & { applicationId: string; title: string; locationId: string };

export interface SettingsState {
    settings?: SettingsStateSettings;
    merchantId: string;
    error: string;
    isLoading: boolean;
    isInitiallyLoaded: boolean;
    timeslots?: Timeslot[];
    loadingImage?: string;
}

export const initialState: SettingsState = {
    settings: undefined,
    error: '',
    merchantId: '',
    isLoading: true,
    isInitiallyLoaded: false
};

export const loadSettings = createAsyncThunk(
    'settings/loadSettings',
    async ({ merchantId, locationId }: { merchantId: string; locationId?: string }) => {
        const tenantData = await settingsApi.getTenant(merchantId);
        const data = await settingsApi.getSettings(merchantId, locationId);
        updateLocalSettings(merchantId, tenantData.platform.applicationId);
        return {
            merchantId,
            settings: {
                ...data,
                locationId,
                googlePayEnabled: !!data.googlePayWebEnabled,
                applePayEnabled: !!data.applePayWebEnabled,
                title: tenantData.title,
                applicationId: tenantData.platform.applicationId
            } as SettingsStateSettings
        };
    }
);

export const loadTimeslots = createAsyncThunk(
    'settings/loadTimeslots',
    async (
        {
            locationId,
            timeslot,
            timeslotDoesntExistMessage = ''
        }: {
            locationId: string;
            timeslot?: Timeslot;
            timeslotDoesntExistMessage?: string;
        },
        { dispatch }
    ) => {
        const result = await timeslotsApi.getTimeslots(locationId);
        const timeslots = result.items.filter(item => item.available);
        if (
            !!timeslot &&
            !timeslots.some(item => item.start === timeslot.start && item.end === timeslot.end)
        ) {
            // Cross-slice: reset basket timeslot
            dispatch(setTimeslot(undefined));
            enqueueSnackbar(timeslotDoesntExistMessage, { variant: 'warning' });
        }
        return timeslots;
    }
);

export const checkLoadingImage = createAsyncThunk('settings/checkLoadingImage', async () => {
    const merchantId = getLocalMerchantId();
    if (!merchantId) {
        return undefined;
    }
    const image = `${getLoadingImagesFolder(merchantId)}/spinner`;
    const result = await fetch(image);
    if (!result.ok) {
        throw new Error("Can't load spinner");
    }
    return image;
});

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        // Used by BasketTimeslotPicker which fetches timeslots directly
        setTimeslots(state, action: PayloadAction<Timeslot[]>) {
            state.timeslots = action.payload;
            state.error = '';
        },
        setTimeslotsError(state, action: PayloadAction<string>) {
            state.timeslots = undefined;
            state.error = action.payload;
        }
    },
    extraReducers: builder => {
        builder
            // loadSettings
            .addCase(loadSettings.pending, state => {
                state.isLoading = true;
            })
            .addCase(loadSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = '';
                state.settings = action.payload.settings;
                state.isInitiallyLoaded = true;
                state.merchantId = action.payload.merchantId;
            })
            .addCase(loadSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.settings = undefined;
                state.error = action.error.message ?? '';
                state.merchantId = '';
                logger.error(action.error);
                resetLocalSettings();
            })
            // loadTimeslots
            .addCase(loadTimeslots.fulfilled, (state, action) => {
                state.timeslots = action.payload;
                state.error = '';
            })
            .addCase(loadTimeslots.rejected, (state, action) => {
                state.timeslots = undefined;
                state.error = action.error.message ?? '';
                logger.error(action.error);
            })
            // checkLoadingImage
            .addCase(checkLoadingImage.fulfilled, (state, action) => {
                state.loadingImage = action.payload;
            })
            .addCase(checkLoadingImage.rejected, state => {
                state.loadingImage = undefined;
            });
    }
});

export const { setTimeslots, setTimeslotsError } = settingsSlice.actions;
export default settingsSlice.reducer;
